import { getConfig, klinikenApi } from "@/api";
import { ActionReturn } from "@/components/forms/Form/types";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    subsetGetter,
    SubsetGetterProps,
    subsetRemover,
    Subsets,
    subsetSetter,
    subsetUpdateSetter,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import type { AxiosResponse } from "axios";
import cloneDeep from "lodash/cloneDeep";

export type Kallelsemall = {
    id: number | string;
    namn?: string;
    body?: unknown;
    typ?: string;
    [key: string]: unknown;
};

export type KallelsemallDisplay = {
    value: Kallelsemall["id"];
    label: string;
    body?: unknown;
    namn?: string;
    typ?: string;
    [key: string]: unknown;
};

type State = BaseState & {
    kallelsemall: Map<Kallelsemall["id"], Kallelsemall>;
};

type Getters = {
    kallelsemallById: (data: { id: Kallelsemall["id"] }) => Kallelsemall | null;
};

export const convertKallelsemallData: (args: { kallelsemall?: Kallelsemall }) => KallelsemallDisplay | null = ({
    kallelsemall,
}) => {
    if (!kallelsemall) {
        return null;
    }

    return {
        ...kallelsemall,
        value: kallelsemall.id,
        label: kallelsemall.namn ?? "",
    };
};

const getDefaultState: () => State = () => {
    return {
        kallelsemall: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "kallelsemall";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        kallelsemallar: (state: State) => [...state.kallelsemall.values()],
        kallelsemallSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertKallelsemallData({ kallelsemall: singleResult }));
            }

            return result;
        },
        kallelsemallById:
            (state: State) =>
            ({ id }: { id?: Kallelsemall["id"] }) => {
                if (!id) {
                    return null;
                }

                return state.kallelsemall.get(id) ?? null;
            },
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        // TODO Ändra namn till plural
        addKallelsemall: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                // TODO Ändra namn till plural
                kallelsemall = [],
                total,
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                kallelsemall: Kallelsemall[];
                total?: number;
            }
        ) =>
            subsetSetter<State, Kallelsemall>({
                offset,
                subsetName,
                state,
                resetSubset,
                total,
                collectionName: moduleName,
                items: kallelsemall,
            }),
        // TODO Ändra namn till plural
        updateKallelsemall: (
            state: State,
            {
                // TODO Ändra namn till plural
                kallelsemall,
                offset,
                subsetName,
            }: {
                kallelsemall: Kallelsemall[];
                offset: number;
                subsetName: keyof Subsets;
            }
        ) =>
            subsetUpdateSetter<State, Kallelsemall>({
                offset,
                state,
                collectionName: moduleName,
                items: kallelsemall,
            }),
        // TODO Ändra namn till plural
        removeKallelsemall: (state: State, { ids }: { ids: string[] | number[] }) => {
            subsetRemover<State>({
                state,
                ids,
                collectionName: moduleName,
            });
        },
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        getKallelsemallById: async (
            {
                getters,
                commit,
                state,
            }: BaseActionsProps<State> & {
                getters: Getters;
            },
            {
                id,
                forceRefresh,
            }: {
                id: Kallelsemall["id"];
                forceRefresh?: boolean;
            }
        ) => {
            const found = getters.kallelsemallById({ id });

            if (!forceRefresh && found) {
                return found;
            }

            try {
                const { data } = await klinikenApi.get<Kallelsemall>(`/kallelser/mallar/${id}/`, getConfig());

                if (data) {
                    commit("addKallelsemall", {
                        offset: Object.keys(state.subsets.default.ids).length,
                        subsetName: "default",
                        kallelsemall: [data],
                    });

                    return data;
                }

                return null;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        fetchKallelsemallar: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                page = 1,
                limit = 20,
            }: {
                displayMode?: boolean;
                filterParams?: FilterParams;
                page?: number;
                limit?: number;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Kallelsemall>({
                    filterParams,
                    state,
                    page,
                    limit,
                    collectionName: moduleName,
                    apiUrl: "kallelser/mallar/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addKallelsemall", {
                    subsetName,
                    offset: resultOffset,
                    kallelsemall: results,
                    total,
                });
                console.log("Fetched kallelsemallar", {
                    results,
                    total,
                    nextPage,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertKallelsemallData({ kallelsemall: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        async createKallelsemall(
            { state, commit }: BaseActionsProps<State>,
            {
                kallelsemall,
            }: {
                kallelsemall: Kallelsemall;
            }
        ): Promise<ActionReturn> {
            try {
                const { data } = await klinikenApi.post("/kallelser/mallar/", kallelsemall, getConfig());

                commit("addKallelsemall", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    kallelsemall: [data],
                });

                commit("addKallelsemall", {
                    offset: Object.keys(state.subsets.default.ids).length,
                    subsetName: "default",
                    kallelsemall: [data],
                });

                return data;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        async updateKallelsemall(
            { state, commit }: BaseActionsProps<State>,
            {
                kallelsemall,
            }: {
                kallelsemall: Kallelsemall;
            }
        ): Promise<ActionReturn> {
            try {
                const { data } = await klinikenApi.put(
                    `/kallelser/mallar/${kallelsemall.id}/`,
                    kallelsemall,
                    getConfig()
                );

                commit("updateKallelsemall", {
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                    subsetName: "newlyUpdated",
                    kallelsemall: [data],
                });

                return data;
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        async deleteKallelsemall(
            { commit }: BaseActionsProps<State>,
            { id }: { id: string | number }
        ): Promise<AxiosResponse | undefined> {
            try {
                const response = await klinikenApi.delete(`/kallelser/mallar/${id}/`, getConfig());

                if (response?.status === 204) {
                    commit("removeKallelsemall", { ids: [id] });
                }

                return response;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
